import { tokenExpiry } from "./storage.js";

/**
 * The two decisions behind a session that expires under a visitor's feet.
 *
 * 🔴 THEY LIVE HERE RATHER THAN IN index.ts BECAUSE index.ts CANNOT BE TESTED.
 *    It runs on import and reaches the network, which is why boot.test.ts is a
 *    set of source assertions and says so. That trade is fine for wiring. It is
 *    not fine for the rules below, because both were wrong in production and a
 *    source assertion cannot tell you a rule is wrong, only that it is present.
 *
 * 🔴 WHAT WAS WRONG: session tokens live 15 minutes, and the install pattern
 *    every customer copies calls `Verb.identify(token)` ONCE on load. So every
 *    identified visitor with a tab open past the quarter hour had their next
 *    question refused with a 401, and the widget rendered the server's own
 *    words for it. Vishnu was shown "Invalid session token" in red,
 *    mid-conversation, on askverb.com's own dashboard.
 */

/**
 * How close to expiry counts as expired.
 *
 * A turn takes seconds, and fetching a replacement token is another round trip.
 * A token with less than a minute left is one that expires mid-answer, which is
 * worse than one that was already dead: the visitor watched it start.
 */
export const REFRESH_WITHIN_SECONDS = 60;

/**
 * Whether the token in hand is worth sending.
 *
 * 🔴 NO TOKEN IS NOT STALE. Plenty of sites identify nobody, and an anonymous
 *    visitor is a supported state, not a thing to go and repair before every
 *    turn. Returning true here would mean asking a host that has no answer,
 *    on every question, forever.
 *
 * 🔴 UNREADABLE CLAIMS ARE STALE. A token whose `exp` cannot be read is one we
 *    know nothing about, and the safe reading of "unknown" for a credential is
 *    "replace it". The cost of being wrong is one extra fetch; the cost the
 *    other way is the failure this file exists to end.
 */
export function isStale(token: string | undefined, nowSeconds = Date.now() / 1000): boolean {
  if (!token) return false;
  const exp = tokenExpiry(token);
  if (exp === null) return true;
  return exp - nowSeconds < REFRESH_WITHIN_SECONDS;
}

/**
 * Whether to ask the host who this is before sending a turn.
 *
 * 🔴 THE WIDGET USED TO GO SILENTLY ANONYMOUS AND NEVER RECOVER. `isStale` was
 *    the only thing gating the pre-turn refresh, and it answers "is the token I
 *    HOLD worth sending". It says false for no token at all, correctly, because
 *    anonymous is a supported state. But that left NO path from "no token" back
 *    to "identified": the stale check declined to ask, and the 401 retry never
 *    fired either, because an anonymous turn is not refused. It succeeds.
 *
 *    So once the token went away on a site that CAN identify people, every
 *    following turn was anonymous, forever, until the host thought to call
 *    `identify()` again. Nothing errored and nothing logged.
 *
 * 🔴 AND IT LOOKED LIKE A WRITE BUG, WHICH IS WHY IT SURVIVED SO LONG. Reads
 *    kept working, so identity looked fine. On a `run: adapter` site they run
 *    in the visitor's own browser under the host's own cookies, so they return
 *    that person's real data whether or not WE know who they are. Only writes
 *    noticed, because an anonymous turn gets the read-only system prompt and
 *    the model declines in prose without ever calling the tool. Reported from
 *    the cal.com integration, 2026-09-14: correct data read back, then "you
 *    need to sign in" on the write, and no request reaching their server at
 *    all.
 *
 * 🔴 ASKING IS ONLY FREE WHEN THERE IS SOMEBODY TO ASK. A site that supplied
 *    `getSessionToken` has said it can answer this; calling it costs one
 *    same-origin request and usually returns a cached session. A site that
 *    never supplied one has no answer, so asking every turn would be the
 *    "forever" loop `isStale` was right to avoid.
 */
export function shouldAskHost(
  token: string | undefined,
  hasSessionTokenGetter: boolean,
  nowSeconds = Date.now() / 1000,
): boolean {
  if (!token) return hasSessionTokenGetter;
  return isStale(token, nowSeconds);
}

/**
 * What a VISITOR is told when a turn is refused before it starts.
 *
 * 🔴 NEVER THE SERVER'S OWN WORDS. This renders on somebody else's website in
 *    front of their customers, and the server writes for whoever installed the
 *    widget. Those are different readers with different needs, and conflating
 *    them put "Invalid session token" on a real page: it told the person
 *    nothing they could act on, and it reads like a security incident on a
 *    product sold on trust.
 *
 *    The detail is not thrown away. It goes to the console, where the developer
 *    is, and the dashboard records the failure either way.
 */
export function visitorMessage(status: number, afterFreshToken = false): string {
  /**
   * 🔴 A REFRESH IS ONLY THE ANSWER WHEN A REFRESH CAN WORK. Telling everybody
   *    to refresh made the worst case an infinite loop: on a site whose signing
   *    key does not match its widget key, EVERY token is refused, including
   *    every token a refresh produces. The cal.com integrator refreshed,
   *    received the identical sentence, refreshed again, and only found the
   *    real cause by opening the console. Advice that cannot work is worse
   *    than no advice, because it looks like progress.
   *
   *    The two cases are told apart without guessing: the turn path already
   *    mints a fresh token and retries once. If THAT is refused too, the
   *    credential is not expired, it is wrong, and nothing the visitor does
   *    will change it. So they are told plainly that it is not their end,
   *    rather than sent to do the one thing that cannot help.
   */
  if (status === 401) {
    if (afterFreshToken) {
      return "This assistant could not confirm who you are. Nothing is wrong on your side, the site needs to fix its setup.";
    }
    // 🔴 The one the visitor can actually fix, so it says what to do rather than
    //    what went wrong. They do not know what a session token is and should
    //    never need to.
    return "Your session ended. Refresh the page and ask again.";
  }
  if (status === 404) return "This assistant is not available right now.";
  if (status === 429) return "That is a lot of questions at once. Give it a moment.";
  if (status >= 500) return "The assistant is having trouble. Try again in a moment.";
  return "That did not go through. Try again.";
}

/**
 * What a visitor is told when the request never got a status at all.
 *
 * 🔴 ONE SENTENCE WAS COVERING FOUR DIFFERENT FAILURES. "Could not reach the
 *    assistant" was the fallback for anything that was not an HTTP error, which
 *    is the browser being offline, an extension blocking the request before it
 *    left the page, a cross-origin refusal, and a stream that died mid-answer.
 *    Those are not the same event and only one of them is ours.
 *
 *    Being offline is the one the visitor can see for themselves and act on, so
 *    it gets its own sentence. The rest stay deliberately vague to the visitor
 *    and specific in the console, because a person on somebody else's website
 *    cannot act on "your CORS preflight failed" and the developer needs exactly
 *    that. Same split as visitorMessage above.
 *
 * 🔴 navigator.onLine IS ONLY TRUSTED WHEN IT SAYS NO. A false is reliable: the
 *    OS has no route. A true means "an interface is up", not that the internet
 *    works, so it is never used to claim the connection is fine.
 */
export function transportMessage(online = navigator.onLine): string {
  if (!online) return "You appear to be offline. Check your connection and ask again.";
  return "That did not reach the assistant. Try again in a moment.";
}
